import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import FunctionalButton from "components/layout/FunctionalButton";
import MapEditor, {
    brushWidthFromBrushSlider,
} from "components/navigation/map/MapEditor";
import { useMap } from "providers/navigation/MapProvider";
import { useToast } from "providers/ToastProvider";
import {
    clearPersistedStorageStcmEdit,
    fetchStcmFilesList,
    parseEditedBmpsFromResponse,
    persistStorageStcmEditFromLocation,
    sanitizeMapEditorSaveSuffix,
    storageMapRowLabel,
    storageRelPathDirAndStem,
} from "lib/consts/stcmMapList";
import { existingStorageMapStemSet } from "lib/protocols/mapLoad";

import { ReactComponent as R2C2Logo } from "assets/svgs/R2C2Logo.svg";
import { ReactComponent as ArrowLeftIcon } from "assets/svgs/ArrowLeft.svg";
import { ReactComponent as BinIcon } from "assets/svgs/Editor/bin.svg";
import { ReactComponent as EraserIcon } from "assets/svgs/Editor/eraser.svg";
import { ReactComponent as FinishIcon } from "assets/svgs/Editor/finish.svg";
import { ReactComponent as PencilIcon } from "assets/svgs/Editor/pencil.svg";
import { ReactComponent as LineIcon } from "assets/svgs/Editor/line.svg";
import { ReactComponent as UndoIcon } from "assets/svgs/Editor/undo.svg";
import { ReactComponent as MagnifierIcon } from "assets/svgs/Editor/magnifier.svg";
import { ReactComponent as PanIcon } from "assets/svgs/Editor/pan.svg";
import { ReactComponent as PixelEraserIcon } from "assets/svgs/Editor/pixeleraser.svg";

const MapEditorPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { mapMetadata } = useMap();
    const { addToast } = useToast();
    const storageEdit = location.state?.storageStcmEdit;
    const storageRelPath =
        typeof storageEdit?.relPath === "string" ? storageEdit.relPath : "";
    const mapManagerRestore = location.state?.mapManagerRestore;
    const titleSuffix = storageRelPath ? ` — ${storageMapRowLabel(storageRelPath)}` : "";

    const { dir: originalDir, stem: originalStem } = useMemo(
        () =>
            typeof storageEdit?.relPath === "string"
                ? storageRelPathDirAndStem(storageEdit.relPath)
                : { dir: "", stem: "" },
        [storageEdit?.relPath],
    );

    const [drawMode, setDrawMode] = useState(false);
    const [lineMode, setLineMode] = useState(false);
    const [eraseMode, setEraseMode] = useState(false);
    /** Tap whole stroke — vs pixel eraser brush. */
    const [pixelEraseMode, setPixelEraseMode] = useState(false);
    const [panMode, setPanMode] = useState(false);
    const [brushSize, setBrushSize] = useState(50);
    /** @type {import("components/navigation/map/MapEditor").MapEditorPenColor} */
    const [penColor, setPenColor] = useState("black");
    const [saveSuffix, setSaveSuffix] = useState("");
    const [existingStemSet, setExistingStemSet] = useState(() => new Set());
    const [stemsListLoading, setStemsListLoading] = useState(false);
    const [stemsListError, setStemsListError] = useState(false);
    const [saveBusy, setSaveBusy] = useState(false);
    const [sketchToolbar, setSketchToolbar] = useState({ canUndo: false });
    /** Pixel size of the storage canvas and whether 1 px ≈ 1 map cell (for brush ↔ meters hint). */
    const [storageCanvasDims, setStorageCanvasDims] = useState(null);
    const editorRef = useRef(null);

    const showStorageTools = Boolean(storageRelPath);

    const brushWidthMetersForSize = useCallback(
        (size) => {
            if (!storageCanvasDims) {
                return null;
            }
            const resolution =
                storageCanvasDims.resolution ??
                (mapMetadata != null &&
                typeof mapMetadata.resolution === "number" &&
                Number.isFinite(mapMetadata.resolution)
                    ? mapMetadata.resolution
                    : null);
            if (resolution == null || resolution <= 0) {
                return null;
            }
            if (storageCanvasDims.cellAligned === false) {
                return null;
            }
            const px = brushWidthFromBrushSlider(
                size,
                storageCanvasDims.w,
                storageCanvasDims.h,
            );
            return px * resolution;
        },
        [storageCanvasDims, mapMetadata],
    );

    const sanitizedSuffix = useMemo(() => sanitizeMapEditorSaveSuffix(saveSuffix), [saveSuffix]);

    const saveBmpRelPath = useMemo(() => {
        if (!originalStem || !sanitizedSuffix) return "";
        const path = `${originalDir}${originalStem}_${sanitizedSuffix}.png`.replace(
            /([^:])\/{2,}/g,
            "$1/",
        );
        if (path.includes("..") || path.startsWith("/")) return "";
        return path;
    }, [originalDir, originalStem, sanitizedSuffix]);

    const proposedStem =
        originalStem && sanitizedSuffix ? `${originalStem}_${sanitizedSuffix}` : "";

    const suffixNonEmpty = sanitizedSuffix.length > 0;
    const nameTaken =
        !stemsListLoading &&
        !stemsListError &&
        Boolean(proposedStem) &&
        existingStemSet.has(proposedStem.toLowerCase());
    const gridCellAligned = storageCanvasDims?.cellAligned === true;
    const canSaveBmp =
        Boolean(saveBmpRelPath) &&
        suffixNonEmpty &&
        !nameTaken &&
        !stemsListLoading &&
        !stemsListError &&
        gridCellAligned;

    const nameTakenToastShownRef = useRef(false);
    useEffect(() => {
        if (nameTaken) {
            if (!nameTakenToastShownRef.current) {
                addToast({
                    type: "warning",
                    duration: 4500,
                    message: "That name is already used. Choose a different suffix.",
                });
                nameTakenToastShownRef.current = true;
            }
        } else {
            nameTakenToastShownRef.current = false;
        }
    }, [nameTaken, addToast]);

    useEffect(() => {
        if (typeof storageEdit?.baseUrl !== "string" || !storageEdit.baseUrl.trim()) {
            setExistingStemSet(new Set());
            setStemsListError(true);
            setStemsListLoading(false);
            return;
        }
        let cancelled = false;
        setStemsListLoading(true);
        setStemsListError(false);
        fetchStcmFilesList(storageEdit.baseUrl)
            .then((data) => {
                if (cancelled) return;
                const edited = parseEditedBmpsFromResponse(data.edited_bmps);
                const bmpPaths = edited.map((e) => e.path);
                setExistingStemSet(existingStorageMapStemSet(data.files || [], bmpPaths));
            })
            .catch(() => {
                if (!cancelled) setStemsListError(true);
            })
            .finally(() => {
                if (!cancelled) setStemsListLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [storageEdit?.baseUrl]);

    useEffect(() => {
        setDrawMode(false);
        setLineMode(false);
        setEraseMode(false);
        setPixelEraseMode(false);
        setPanMode(false);
        setSaveSuffix("");
        setPenColor("black");
        setBrushSize(50);
    }, [storageEdit?.relPath]);

    const maskToolsOpen = drawMode || lineMode || eraseMode || pixelEraseMode;

    useEffect(() => {
        if (storageEdit?.relPath) {
            persistStorageStcmEditFromLocation(location);
        }
    }, [location, storageEdit?.relPath]);

    const goToControl = () => {
        clearPersistedStorageStcmEdit();
        const navState =
            mapManagerRestore?.focusedStcm != null
                ? { mapManagerRestore }
                : undefined;
        if (location.pathname.startsWith("/fleet")) {
            navigate("/fleet/map/live", { state: navState });
        } else {
            navigate("/arc/control", { state: navState });
        }
    };

    const onSaveSuffixChange = (e) => {
        setSaveSuffix(e.target.value.replace(/\s+/g, "_"));
    };

    const onStorageCanvasDimensions = useCallback((dims) => {
        setStorageCanvasDims(dims ?? null);
    }, []);

    const toolBtn = (active) =>
        `box-border flex h-10 min-w-[2.5rem] shrink-0 items-center justify-center rounded-r5 px-2 transition-colors ${
            active ? "control-button-active text-white" : "control-button-type3"
        }`;

    const toolIconCircle = (active) =>
        `box-border flex h-10 w-10 shrink-0 items-center justify-center rounded-r5 transition-colors ${
            active ? "control-button-active text-white" : "control-button-type3"
        }`;

    return (
        <div className="page flex h-full min-h-0 min-w-0 flex-col overflow-hidden bg-black100">
            <header className="flex shrink-0 flex-col bg-chrome text-white">
                <div className="flex w-full min-w-0 border-b border-black80">
                    <div
                        className="flex w-20 shrink-0 items-center justify-center py-[1.375rem]"
                        aria-hidden
                    >
                        <R2C2Logo className="pointer-events-none block h-9 w-12" />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-3 py-[1.375rem] pl-4 pr-5">
                        <p className="min-w-0 truncate text-h3 text-white">
                            Map Editor
                            {titleSuffix ? (
                                <span className="text-h5 font-normal text-black40">{titleSuffix}</span>
                            ) : null}
                        </p>
                        {showStorageTools ? (
                            <div className="flex min-w-0 max-w-full flex-[2] flex-col gap-1 md:max-w-lg">
                                <div className="flex min-w-0 max-w-full items-center gap-2">
                                    <label className="shrink-0 whitespace-nowrap text-h5 text-black40">
                                        Save as
                                    </label>
                                    <div className="flex min-w-0 flex-1 items-center overflow-hidden rounded-r5 border border-black80 bg-black70 px-3 py-2">
                                        <span
                                            className="pointer-events-none shrink-0 cursor-default select-none whitespace-pre text-h5 text-black60"
                                            aria-hidden
                                        >
                                            {originalStem}_
                                        </span>
                                        <input
                                            type="text"
                                            value={saveSuffix}
                                            onChange={onSaveSuffixChange}
                                            title="Suffix for saved map name"
                                            aria-invalid={nameTaken}
                                            className="min-w-0 flex-1 bg-transparent text-h5 text-white outline-none placeholder:text-black60"
                                            spellCheck={false}
                                            autoComplete="off"
                                            placeholder="REQUIRED"
                                            aria-required
                                        />
                                    </div>
                                </div>
                                {stemsListLoading ? (
                                    <p className="text-h5 text-black60">Checking existing names…</p>
                                ) : null}
                                {!stemsListLoading && stemsListError ? (
                                    <p className="text-h5 text-amber-400">
                                        Couldn&apos;t check existing map names. Reload this map before saving.
                                    </p>
                                ) : null}
                                {!stemsListLoading && storageCanvasDims && !gridCellAligned ? (
                                    <p className="text-h5 text-amber-400">
                                        Load the .stcm on Control first so the mask matches the live
                                        map grid ({storageCanvasDims.w}×{storageCanvasDims.h}{" "}
                                        preview only).
                                    </p>
                                ) : null}
                            </div>
                        ) : null}
                        <div className="flex shrink-0 items-center gap-2">
                            {showStorageTools ? (
                                <FunctionalButton
                                    className="rounded-r5 bg-primary2 px-4 py-2 text-h5 hover:bg-primary1 disabled:opacity-50"
                                    title="Save mask"
                                    hoverText={
                                        gridCellAligned
                                            ? "Nav2 keep-out image aligned with static map"
                                            : "Load .stcm on Control for live grid alignment before saving"
                                    }
                                    disabled={saveBusy || !canSaveBmp}
                                    onClick={() => editorRef.current?.saveBmp?.()}
                                />
                            ) : null}
                            <button
                                type="button"
                                title="Control"
                                aria-label="Control"
                                className="control-button-type1 flex !h-10 !w-10 shrink-0 items-center justify-center !border !border-primary2 !bg-primary2 hover:!bg-primary1"
                                onClick={goToControl}
                            >
                                <ArrowLeftIcon className="h-5 w-5 fill-white" />
                            </button>
                        </div>
                    </div>
                </div>
                {showStorageTools ? (
                    <div className="flex w-full min-w-0 border-b border-black80">
                        <div className="w-20 shrink-0" aria-hidden />
                        <div className="grid min-w-0 flex-1 grid-cols-[1fr_auto_1fr] items-center gap-x-2 py-2 pl-4 pr-5">
                            <div className="min-w-0" aria-hidden />
                            <div className="flex flex-wrap items-center justify-center gap-2">
                                <span className="mr-1 hidden text-h5 text-black40 sm:inline">
                                    Keep-out mask
                                </span>
                                <button
                                    type="button"
                                    aria-label="Undo last keep-out stroke"
                                    title="Undo last stroke"
                                    disabled={!sketchToolbar.canUndo}
                                    className={`${toolIconCircle(false)} disabled:cursor-not-allowed disabled:opacity-40`}
                                    onClick={() => editorRef.current?.undoSketch?.()}
                                >
                                    <UndoIcon className="mx-auto block !h-5 !w-5 shrink-0 [&_path]:stroke-current" />
                                </button>
                                <button
                                    type="button"
                                    aria-label={
                                        drawMode
                                            ? "Finish painting keep-out"
                                            : "Paint keep-out (occupied / forbidden)"
                                    }
                                    title={
                                        drawMode
                                            ? "Finish"
                                            : "Paint keep-out — black marks forbidden cells (Nav2 occupied)"
                                    }
                                    className={toolBtn(drawMode)}
                                    onClick={() => {
                                        setEraseMode(false);
                                        setPanMode(false);
                                        setPixelEraseMode(false);
                                        setLineMode(false);
                                        setDrawMode((d) => !d);
                                    }}
                                >
                                    {drawMode ? (
                                        <FinishIcon className="mx-auto block !h-5 !w-5 shrink-0" />
                                    ) : (
                                        <PencilIcon className="mx-auto block !h-5 !w-5 shrink-0" />
                                    )}
                                </button>
                                <button
                                    type="button"
                                    aria-label={
                                        lineMode
                                            ? "Finish straight line tool"
                                            : "Draw straight keep-out line"
                                    }
                                    title={
                                        lineMode
                                            ? "Finish"
                                            : "Straight line — click and drag to draw a line"
                                    }
                                    className={toolBtn(lineMode)}
                                    onClick={() => {
                                        setDrawMode(false);
                                        setEraseMode(false);
                                        setPanMode(false);
                                        setPixelEraseMode(false);
                                        setLineMode((v) => !v);
                                    }}
                                >
                                    {lineMode ? (
                                        <FinishIcon className="mx-auto block !h-5 !w-5 shrink-0" />
                                    ) : (
                                        <LineIcon className="mx-auto block !h-5 !w-5 shrink-0" />
                                    )}
                                </button>
                                <button
                                    type="button"
                                    aria-label={
                                        eraseMode
                                            ? "Stop stroke remove mode"
                                            : "Remove a keep-out stroke by tapping it"
                                    }
                                    title={
                                        eraseMode
                                            ? "Tap a stroke to delete it"
                                            : "Tap a drawn stroke to remove it (whole stroke)"
                                    }
                                    className={toolBtn(eraseMode)}
                                    onClick={() => {
                                        setDrawMode(false);
                                        setLineMode(false);
                                        setPixelEraseMode(false);
                                        setPanMode(false);
                                        setEraseMode((e) => !e);
                                    }}
                                >
                                    <EraserIcon className="mx-auto block !h-5 !w-5 shrink-0" />
                                </button>
                                <button
                                    type="button"
                                    aria-label={
                                        pixelEraseMode
                                            ? "Finish pixel eraser"
                                            : "Pixel eraser — brush white over paint"
                                    }
                                    title={
                                        pixelEraseMode
                                            ? "Finish"
                                            : "Erase by brushing (white / free space), same brush size"
                                    }
                                    className={toolBtn(pixelEraseMode)}
                                    onClick={() => {
                                        setDrawMode(false);
                                        setLineMode(false);
                                        setEraseMode(false);
                                        setPanMode(false);
                                        setPixelEraseMode((v) => !v);
                                    }}
                                >
                                    <PixelEraserIcon className="mx-auto block !h-5 !w-5 shrink-0 [&_path]:fill-current" />
                                </button>
                                <button
                                    type="button"
                                    aria-label={
                                        panMode ? "Stop panning" : "Pan map (drag to scroll)"
                                    }
                                    title={
                                        panMode
                                            ? "Finish panning"
                                            : "Pan — drag to move the map view"
                                    }
                                    className={toolBtn(panMode)}
                                    onClick={() => {
                                        setDrawMode(false);
                                        setLineMode(false);
                                        setEraseMode(false);
                                        setPixelEraseMode(false);
                                        setPanMode((p) => !p);
                                    }}
                                >
                                    <PanIcon className="mx-auto block !h-5 !w-5 shrink-0 [&_path]:fill-white" />
                                </button>
                                <button
                                    type="button"
                                    aria-label="Clear entire mask overlay"
                                    title="Clear all keep-out strokes"
                                    disabled={!sketchToolbar.canUndo}
                                    className={`${toolIconCircle(false)} disabled:cursor-not-allowed disabled:opacity-40`}
                                    onClick={() => editorRef.current?.clearAllSketch?.()}
                                >
                                    <BinIcon className="mx-auto block !h-5 !w-5 shrink-0" />
                                </button>
                                {maskToolsOpen ? (
                                    <>
                                        <span className="pl-2 pr-1 text-h5 text-black40 sm:pl-3">
                                            Brush
                                        </span>
                                        <div className="flex min-w-[7rem] max-w-[11rem] flex-1 items-center gap-2 sm:min-w-[10rem]">
                                            <span
                                                className="h-2 w-2 shrink-0 rounded-full bg-white ring-1 ring-black60"
                                                title="Fine"
                                                aria-hidden
                                            />
                                            <label className="flex min-w-0 flex-1 items-center">
                                                <span className="sr-only">Brush size</span>
                                                <input
                                                    type="range"
                                                    min={0}
                                                    max={100}
                                                    value={brushSize}
                                                    onChange={(e) =>
                                                        setBrushSize(Number(e.target.value))
                                                    }
                                                    className="h-2 w-full min-w-0 cursor-pointer accent-primary2"
                                                    title={
                                                        brushWidthMetersForSize(brushSize) !=
                                                        null
                                                            ? `Stroke width ~${brushWidthMetersForSize(brushSize).toFixed(2)} m (live map grid)`
                                                            : "Brush size — load .stcm on Control for live grid alignment"
                                                    }
                                                    aria-valuemin={0}
                                                    aria-valuemax={100}
                                                    aria-valuenow={brushSize}
                                                />
                                            </label>
                                            <span
                                                className="h-3.5 w-3.5 shrink-0 rounded-full bg-white ring-1 ring-black60"
                                                title="Thick"
                                                aria-hidden
                                            />
                                        </div>
                                        {drawMode || lineMode ? (
                                            <>
                                                <span className="pl-2 pr-1 text-h5 text-black40 sm:pl-3">
                                                    Color
                                                </span>
                                                <div className="flex items-center gap-1.5">
                                                    {[
                                                        {
                                                            id: "black",
                                                            title: "Black — keep-out (occupied)",
                                                            swatchClass: "bg-black",
                                                        },
                                                        {
                                                            id: "grey",
                                                            title: "Grey — intermediate / soft barrier",
                                                            swatchClass: "bg-[#7f7f7f]",
                                                        },
                                                        {
                                                            id: "white",
                                                            title: "White — free space (passable)",
                                                            swatchClass:
                                                                "border border-black60 bg-white",
                                                        },
                                                    ].map(({ id, title, swatchClass }) => (
                                                        <button
                                                            key={id}
                                                            type="button"
                                                            title={title}
                                                            aria-label={title}
                                                            aria-pressed={penColor === id}
                                                            className={`h-8 w-8 shrink-0 rounded-full ring-2 ring-[#454545] ${swatchClass} ${
                                                                penColor === id
                                                                    ? "ring-primary2 ring-offset-2 ring-offset-black100"
                                                                    : ""
                                                            }`}
                                                            onClick={() => setPenColor(id)}
                                                        />
                                                    ))}
                                                </div>
                                            </>
                                        ) : null}
                                    </>
                                ) : null}
                            </div>
                            <div className="flex min-w-0 flex-nowrap items-center justify-end justify-self-end gap-1">
                                <span className="pr-1 text-h5 text-black40">Zoom</span>
                                <button
                                    type="button"
                                    aria-label="Zoom in"
                                    title="Zoom in (pinch or Ctrl+scroll)"
                                    className={toolIconCircle(false)}
                                    onClick={() => editorRef.current?.zoomViewIn?.()}
                                >
                                    <MagnifierIcon className="mx-auto block !h-5 !w-5 shrink-0 scale-110 [&_path]:fill-current" />
                                </button>
                                <button
                                    type="button"
                                    aria-label="Zoom out"
                                    title="Zoom out (pinch or Ctrl+scroll)"
                                    className={toolIconCircle(false)}
                                    onClick={() => editorRef.current?.zoomViewOut?.()}
                                >
                                    <MagnifierIcon className="mx-auto block !h-5 !w-5 shrink-0 scale-90 opacity-90 [&_path]:fill-current" />
                                </button>
                                <button
                                    type="button"
                                    aria-label="Reset zoom to fit"
                                    title="Fit to panel"
                                    className={`${toolBtn(false)} !px-3`}
                                    onClick={() => editorRef.current?.resetViewZoom?.()}
                                >
                                    <span className="text-h5">Fit</span>
                                </button>
                            </div>
                        </div>
                    </div>
                ) : null}
            </header>
            <div className="relative min-h-0 min-w-0 flex-1">
                <MapEditor
                    ref={editorRef}
                    drawMode={drawMode}
                    eraseMode={eraseMode}
                    pixelEraseMode={pixelEraseMode}
                    lineMode={lineMode}
                    panMode={panMode}
                    saveBmpRelPath={saveBmpRelPath}
                    brushSize={brushSize}
                    penColor={penColor}
                    onSaveBusyChange={setSaveBusy}
                    onSketchToolbarState={setSketchToolbar}
                    onStorageCanvasDimensions={onStorageCanvasDimensions}
                />
            </div>
        </div>
    );
};

export default MapEditorPage;
